// The attachments a user turn carried, shown inside its bubble.
//
// The thread is the only place the clinic can check what it sent: the data URL
// lives in this turn and nowhere else (D-B2), and the persisted transcript
// keeps a text stub. A chip names the file, its size and — for an image — shows
// the picture the model read.

import { FileText, Image as ImageIcon, File as FileIcon, Music } from "lucide-react";
import { dataUrlByteSize, formatFileSize, modalityOf } from "@/lib/media-prep";
import type { FilePartView } from "./message-parts";

function iconFor(mediaType: string) {
  switch (modalityOf(mediaType)) {
    case "image":
      return ImageIcon;
    case "pdf":
      return FileText;
    case "audio":
      return Music;
    default:
      return FileIcon;
  }
}

export function MessageAttachments({ files }: { files: readonly FilePartView[] }) {
  if (files.length === 0) return null;

  return (
    <ul className="mb-2 flex flex-wrap gap-2" aria-label="Anexos enviados">
      {files.map((file, index) => {
        const Icon = iconFor(file.mediaType);
        const isImage = modalityOf(file.mediaType) === "image";
        const size = dataUrlByteSize(file.url);

        return (
          <li
            key={`${file.filename}-${index}`}
            className="flex items-center gap-2 rounded-xl border border-app-border bg-app-surface px-2.5 py-1.5"
          >
            {isImage && file.url ? (
              <img
                src={file.url}
                alt={file.filename}
                className="size-8 shrink-0 rounded-md object-cover"
              />
            ) : (
              <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-brand-soft">
                <Icon className="size-4 text-brand" aria-hidden="true" />
              </span>
            )}
            <span className="min-w-0">
              <span className="block max-w-[150px] truncate text-xs font-medium text-app-fg-strong">
                {file.filename}
              </span>
              <span className="block text-[10px] text-app-fg-muted">
                {formatFileSize(size)}
              </span>
            </span>
          </li>
        );
      })}
    </ul>
  );
}
